// src/components/ImportFilesByInventory.jsx
// Rattache des images et des PDF aux œuvres déjà importées, d'après leur numéro d'inventaire.
// Un ZIP (avec ou sans dossiers) ou une sélection de fichiers : « REF-12.jpg », « REF-12 détail.jpg », dossier « REF-12/… ».
// Étapes : choisir → contrôle (rien n'est envoyé) → envoi → compte rendu et rapport CSV. Une reprise ne duplique rien.
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "../lib/supabaseClient";
import { reportToCsv } from "../utils/importHelpers";
import { matchFilesToArtworks, groupByArtwork, docTypeFromName, baseName, isNoise } from "../utils/fileMatching";

const box = { border: "1px solid #e5e5e5", borderRadius: 2, padding: "1.25rem" };
const btn = (primary) => ({
  padding: "0.4rem 1.25rem", borderRadius: 2, fontSize: "0.85rem", cursor: "pointer", fontFamily: "Georgia, serif",
  border: "1px solid #1a1a1a", background: primary ? "#1a1a1a" : "#fff", color: primary ? "#fff" : "#1a1a1a",
});

const COPY = {
  fr: {
    title: "Rattacher des images et des documents par numéro d'inventaire",
    intro1: "Après l'import de votre tableur, déposez un ", zip: "ZIP",
    intro2: " (ou plusieurs fichiers) d'images et de PDF dont le nom contient le numéro d'inventaire :",
    examples: ["REF-12.jpg", "REF-12_détail.jpg", "REF-12 certificat.pdf"], orFolder: "ou un dossier", folder: "REF-12/",
    intro3: "Rien n'est envoyé avant votre validation ; ce qui est déjà rattaché n'est pas dupliqué.",
    noInventory: "Cette collection ne contient aucune œuvre avec un numéro d'inventaire : importez d'abord votre tableur, avec la colonne « numéro d'inventaire ».",
    readFail: (m) => `Lecture impossible : ${m}.`, unreadable: "fichier illisible", sendFail: "envoi impossible", sheet: "(fiche)",
    pick: "Cliquez pour choisir un ZIP ou des fichiers", analyzing: "Analyse en cours…",
    toAttach: (i, p) => [`${i}`, " image(s) et ", `${p}`, " PDF à rattacher"], toArtworks: (a, t) => [`${a}`, ` œuvre(s) sur ${t}`], toWord: "à",
    already: (n) => `${n} déjà rattaché(s) : ignoré(s)`, unattached: " fichier(s) non rattaché(s)",
    colRef: "N° d'inventaire", colWork: "Œuvre", colImages: "Images", colPdf: "PDF",
    pUnmatched: (n, ex) => `${n} fichier(s) sans numéro d'inventaire reconnu (ex. ${ex}).`,
    pAmbiguous: (n) => `${n} fichier(s) pouvant correspondre à plusieurs œuvres : ignoré(s).`,
    pUnsupported: (n) => `${n} fichier(s) dans un format non pris en charge (TIFF, HEIC…) : à convertir en JPEG ou PNG.`,
    pTooLarge: (n) => `${n} fichier(s) de plus de 15 Mo : ignoré(s).`,
    attach: (n) => `Rattacher ${n} fichier(s)`, report: "Télécharger le rapport (CSV)", cancel: "Annuler",
    uploading: (c, t, l) => `Envoi en cours… ${c} / ${t} · ${l}`,
    done: (i, d, a) => `✓ ${i} image(s) et ${d} document(s) rattaché(s) à ${a} œuvre(s).`,
    errs: (n, ref, msg) => `${n} fichier(s) n'ont pas pu être envoyés (par exemple ${ref} : ${msg}). Relancez le même ZIP : ce qui est déjà rattaché n'est pas dupliqué.`,
    again: "Nouveau rattachement", view: "Voir ma collection →",
    rUnmatched: "Sans œuvre correspondante", rAmbiguous: "Plusieurs œuvres possibles", rUnsupported: "Format non pris en charge", rTooLarge: "Fichier trop lourd (15 Mo max)", rError: "Erreur d'envoi",
  },
  en: {
    title: "Attach images and documents by inventory number",
    intro1: "After importing your spreadsheet, drop a ", zip: "ZIP",
    intro2: " (or several files) of images and PDFs whose name contains the inventory number:",
    examples: ["REF-12.jpg", "REF-12_detail.jpg", "REF-12 certificate.pdf"], orFolder: "or a folder", folder: "REF-12/",
    intro3: "Nothing is uploaded before you confirm; what is already attached is not duplicated.",
    noInventory: "This collection has no work with an inventory number: import your spreadsheet first, with the “inventory number” column.",
    readFail: (m) => `Could not read: ${m}.`, unreadable: "unreadable file", sendFail: "upload failed", sheet: "(record)",
    pick: "Click to choose a ZIP or files", analyzing: "Analysing…",
    toAttach: (i, p) => [`${i}`, " image(s) and ", `${p}`, " PDF(s) to attach"], toArtworks: (a, t) => [`${a}`, ` work(s) out of ${t}`], toWord: "to",
    already: (n) => `${n} already attached: skipped`, unattached: " file(s) not attached",
    colRef: "Inventory no.", colWork: "Work", colImages: "Images", colPdf: "PDF",
    pUnmatched: (n, ex) => `${n} file(s) with no recognised inventory number (e.g. ${ex}).`,
    pAmbiguous: (n) => `${n} file(s) that could match several works: skipped.`,
    pUnsupported: (n) => `${n} file(s) in an unsupported format (TIFF, HEIC…): convert to JPEG or PNG.`,
    pTooLarge: (n) => `${n} file(s) over 15 MB: skipped.`,
    attach: (n) => `Attach ${n} file(s)`, report: "Download the report (CSV)", cancel: "Cancel",
    uploading: (c, t, l) => `Uploading… ${c} / ${t} · ${l}`,
    done: (i, d, a) => `✓ ${i} image(s) and ${d} document(s) attached to ${a} work(s).`,
    errs: (n, ref, msg) => `${n} file(s) could not be uploaded (for example ${ref}: ${msg}). Run the same ZIP again: what is already attached is not duplicated.`,
    again: "New attachment run", view: "View my collection →",
    rUnmatched: "No matching work", rAmbiguous: "Several possible works", rUnsupported: "Unsupported format", rTooLarge: "File too large (15 MB max)", rError: "Upload error",
  },
  de: {
    title: "Bilder und Dokumente per Inventarnummer zuordnen",
    intro1: "Legen Sie nach dem Import Ihrer Tabelle ein ", zip: "ZIP",
    intro2: " (oder mehrere Dateien) mit Bildern und PDFs ab, deren Name die Inventarnummer enthält:",
    examples: ["REF-12.jpg", "REF-12_Detail.jpg", "REF-12 Zertifikat.pdf"], orFolder: "oder einen Ordner", folder: "REF-12/",
    intro3: "Vor Ihrer Bestätigung wird nichts hochgeladen; bereits Zugeordnetes wird nicht doppelt angelegt.",
    noInventory: "Diese Sammlung enthält kein Werk mit Inventarnummer: Importieren Sie zuerst Ihre Tabelle mit der Spalte „Inventarnummer“.",
    readFail: (m) => `Lesen nicht möglich: ${m}.`, unreadable: "Datei nicht lesbar", sendFail: "Upload fehlgeschlagen", sheet: "(Datensatz)",
    pick: "Klicken, um ein ZIP oder Dateien auszuwählen", analyzing: "Analyse läuft…",
    toAttach: (i, p) => [`${i}`, " Bild(er) und ", `${p}`, " PDF zuzuordnen"], toArtworks: (a, t) => [`${a}`, ` Werk(e) von ${t}`], toWord: "zu",
    already: (n) => `${n} bereits zugeordnet: übersprungen`, unattached: " Datei(en) nicht zugeordnet",
    colRef: "Inventar-Nr.", colWork: "Werk", colImages: "Bilder", colPdf: "PDF",
    pUnmatched: (n, ex) => `${n} Datei(en) ohne erkannte Inventarnummer (z. B. ${ex}).`,
    pAmbiguous: (n) => `${n} Datei(en), die zu mehreren Werken passen könnten: übersprungen.`,
    pUnsupported: (n) => `${n} Datei(en) in nicht unterstütztem Format (TIFF, HEIC…): in JPEG oder PNG umwandeln.`,
    pTooLarge: (n) => `${n} Datei(en) über 15 MB: übersprungen.`,
    attach: (n) => `${n} Datei(en) zuordnen`, report: "Bericht herunterladen (CSV)", cancel: "Abbrechen",
    uploading: (c, t, l) => `Upload läuft… ${c} / ${t} · ${l}`,
    done: (i, d, a) => `✓ ${i} Bild(er) und ${d} Dokument(e) ${a} Werk(en) zugeordnet.`,
    errs: (n, ref, msg) => `${n} Datei(en) konnten nicht hochgeladen werden (z. B. ${ref}: ${msg}). Starten Sie dasselbe ZIP erneut: Bereits Zugeordnetes wird nicht doppelt angelegt.`,
    again: "Neue Zuordnung", view: "Meine Sammlung ansehen →",
    rUnmatched: "Kein passendes Werk", rAmbiguous: "Mehrere mögliche Werke", rUnsupported: "Nicht unterstütztes Format", rTooLarge: "Datei zu groß (max. 15 MB)", rError: "Upload-Fehler",
  },
};

const uid = () => `${Date.now()}_${Math.random().toString(36).slice(2)}`;

export default function ImportFilesByInventory({ collectionId, onDone }) {
  const { i18n } = useTranslation();
  const lang = String(i18n?.resolvedLanguage || i18n?.language || "fr").slice(0, 2);
  const c = COPY[lang] || COPY.fr;
  const [step, setStep] = useState("idle"); // idle | analyzing | preview | uploading | done
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");
  const [plan, setPlan] = useState(null);   // { groups, match, noInventory, total }
  const [progress, setProgress] = useState({ current: 0, total: 0, label: "" });
  const [result, setResult] = useState(null);

  async function readEntries(files) {
    const out = [];
    for (const f of files) {
      if (/\.zip$/i.test(f.name)) {
        const JSZip = (await import("jszip")).default;
        const zip = await JSZip.loadAsync(await f.arrayBuffer());
        zip.forEach((relPath, entry) => {
          if (entry.dir || isNoise(relPath)) return;
          const size = entry._data && typeof entry._data.uncompressedSize === "number" ? entry._data.uncompressedSize : 0;
          out.push({ path: relPath, size, getBlob: () => entry.async("blob") });
        });
      } else {
        out.push({ path: f.webkitRelativePath || f.name, size: f.size || 0, getBlob: async () => f });
      }
    }
    return out;
  }

  async function loadArtworks() {
    const rows = [];
    for (let from = 0; ; from += 1000) {
      const { data, error: e } = await supabase
        .from("artworks")
        .select("id, title, inventory_number, images, documents")
        .eq("collection_id", collectionId)
        .not("inventory_number", "is", null)
        .range(from, from + 999);
      if (e) throw e;
      rows.push(...(data || []));
      if (!data || data.length < 1000) break;
    }
    return rows;
  }

  async function handleFiles(fileList) {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setStep("analyzing"); setError(""); setResult(null);
    try {
      const [entries, artworks] = await Promise.all([readEntries(files), loadArtworks()]);
      if (!artworks.length) {
        setError(c.noInventory);
        setStep("idle");
        return;
      }
      const match = matchFilesToArtworks(entries, artworks);
      const groups = groupByArtwork(match.matched);
      setPlan({ match, groups, artworks: artworks.length });
      setStep("preview");
    } catch (e) {
      setError(c.readFail(e?.message || c.unreadable));
      setStep("idle");
    }
  }

  async function upload() {
    if (!plan) return;
    const todo = plan.groups.filter((g) => g.images.length || g.pdfs.length);
    const total = todo.reduce((n, g) => n + g.images.length + g.pdfs.length, 0);
    setStep("uploading");
    let current = 0, imgOk = 0, docOk = 0, artOk = 0;
    const errors = [];

    for (const g of todo) {
      const a = g.artwork;
      const label = a.inventory_number;
      const newImages = [];
      const newDocs = [];
      for (const e of g.images) {
        current += 1; setProgress({ current, total, label: `${label} · ${baseName(e.path)}` });
        try {
          const blob = await e.getBlob();
          const name = String(e.path).split("/").pop();
          const ext = (name.split(".").pop() || "jpg").toLowerCase();
          const path = `${a.id}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
          const { error: upErr } = await supabase.storage.from("artworks-images").upload(path, blob, { upsert: true, contentType: blob.type || "image/jpeg" });
          if (upErr) throw upErr;
          const { data: pub } = supabase.storage.from("artworks-images").getPublicUrl(path);
          newImages.push({ id: `img_${uid()}`, path: pub.publicUrl, url: pub.publicUrl, publicUrl: pub.publicUrl, isPrimary: false, filename: name });
        } catch (err) { errors.push({ ref: label, file: e.path, message: err?.message || c.sendFail }); }
      }
      for (const e of g.pdfs) {
        current += 1; setProgress({ current, total, label: `${label} · ${baseName(e.path)}` });
        try {
          const blob = await e.getBlob();
          const name = String(e.path).split("/").pop();
          const path = `${a.id}/${Date.now()}-${Math.random().toString(36).slice(2)}.pdf`;
          const { error: upErr } = await supabase.storage.from("artworks-documents").upload(path, blob, { upsert: true, contentType: "application/pdf" });
          if (upErr) throw upErr;
          const { data: pub } = supabase.storage.from("artworks-documents").getPublicUrl(path);
          const guess = docTypeFromName(name);
          newDocs.push({ id: `doc_${uid()}`, name: baseName(name), type: guess.type, label: guess.label, file: pub.publicUrl, date: new Date().toISOString() });
        } catch (err) { errors.push({ ref: label, file: e.path, message: err?.message || c.sendFail }); }
      }
      if (!newImages.length && !newDocs.length) continue;

      // Fusion avec l'existant : rien n'est remplacé ; la première image devient principale s'il n'y en avait pas.
      const curImages = Array.isArray(a.images) ? a.images : [];
      let curDocs = [];
      try { curDocs = typeof a.documents === "string" ? JSON.parse(a.documents || "[]") : (a.documents || []); } catch { curDocs = []; }
      if (!Array.isArray(curDocs)) curDocs = [];
      if (!curImages.some((i) => i && i.isPrimary) && newImages.length) newImages[0].isPrimary = true;
      const allImages = [...curImages, ...newImages];
      const allDocs = [...curDocs, ...newDocs];
      const patch = { images: allImages, documents: JSON.stringify(allDocs) };
      const primary = allImages.find((i) => i && i.isPrimary) || allImages[0];
      patch.image_url = primary ? (primary.url || primary.publicUrl || null) : null;
      const { error: saveErr } = await supabase.from("artworks").update(patch).eq("id", a.id);
      if (saveErr) { errors.push({ ref: label, file: c.sheet, message: saveErr.message }); continue; }
      artOk += 1; imgOk += newImages.length; docOk += newDocs.length;
    }

    setResult({ artOk, imgOk, docOk, errors });
    setStep("done");
    onDone?.();
  }

  function downloadReport() {
    if (!plan) return;
    const m = plan.match;
    const entries = [
      ...m.unmatched.map((e) => ({ line: "", title: baseName(e.path), ref: "", type: c.rUnmatched, detail: e.path })),
      ...m.ambiguous.map((e) => ({ line: "", title: baseName(e.path), ref: "", type: c.rAmbiguous, detail: e.path })),
      ...m.unsupported.map((e) => ({ line: "", title: baseName(e.path), ref: "", type: c.rUnsupported, detail: e.path })),
      ...m.tooLarge.map((e) => ({ line: "", title: baseName(e.path), ref: "", type: c.rTooLarge, detail: e.path })),
      ...(result?.errors || []).map((x) => ({ line: "", title: baseName(x.file), ref: x.ref, type: c.rError, detail: x.message })),
    ];
    const blob = new Blob([reportToCsv(entries)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "rattachement-fichiers.csv"; document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  }

  const reset = () => { setStep("idle"); setPlan(null); setResult(null); setError(""); };

  const counts = plan ? {
    images: plan.groups.reduce((n, g) => n + g.images.length, 0),
    pdfs: plan.groups.reduce((n, g) => n + g.pdfs.length, 0),
    artworks: plan.groups.filter((g) => g.images.length || g.pdfs.length).length,
    already: plan.groups.reduce((n, g) => n + g.skipped, 0),
  } : null;
  const problems = plan ? plan.match.unmatched.length + plan.match.ambiguous.length + plan.match.unsupported.length + plan.match.tooLarge.length : 0;

  return (
    <div style={{ ...box, fontFamily: "Georgia, serif" }}>
      <button type="button" onClick={() => setOpen((v) => !v)} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, fontSize: "0.95rem", letterSpacing: "0.04em", color: "#1a1a1a" }}>
        {open ? "▾" : "▸"} {c.title}
      </button>

      {open && (
        <div style={{ marginTop: "1rem" }}>
          <p style={{ fontSize: "0.82rem", color: "#666", lineHeight: 1.6, marginBottom: "0.75rem" }}>
            {c.intro1}<strong>{c.zip}</strong>{c.intro2}
            {" "}<em>{c.examples[0]}</em>, <em>{c.examples[1]}</em>, <em>{c.examples[2]}</em>, {c.orFolder} <em>{c.folder}</em>.
            {" "}{c.intro3}
          </p>

          {error && <p style={{ fontSize: "0.85rem", color: "#8a3a22", marginBottom: "0.75rem" }}>{error}</p>}

          {(step === "idle" || step === "analyzing") && (
            <label style={{ display: "block", border: "1px dashed #c9a961", borderRadius: 2, padding: "1rem", textAlign: "center", cursor: "pointer", background: "#faf9f6", color: "#888", fontSize: "0.88rem" }}>
              <input
                type="file" multiple accept=".zip,.jpg,.jpeg,.png,.webp,.gif,.pdf" style={{ display: "none" }}
                onChange={(e) => handleFiles(e.target.files)}
              />
              {step === "analyzing" ? c.analyzing : c.pick}
            </label>
          )}

          {step === "preview" && plan && counts && (
            <div>
              <div style={{ display: "flex", gap: "1.25rem", flexWrap: "wrap", fontSize: "0.85rem", color: "#444", marginBottom: "0.75rem" }}>
                <span><strong style={{ color: "#2a6a25" }}>{counts.images}</strong>{c.toAttach(counts.images, counts.pdfs)[1]}<strong style={{ color: "#2a6a25" }}>{counts.pdfs}</strong>{c.toAttach(counts.images, counts.pdfs)[3]}</span>
                <span>{c.toWord} <strong>{counts.artworks}</strong>{c.toArtworks(counts.artworks, plan.artworks)[1]}</span>
                {counts.already > 0 && <span style={{ color: "#888" }}>{c.already(counts.already)}</span>}
                <span style={{ color: problems ? "#a06010" : "#2a6a25" }}><strong>{problems}</strong>{c.unattached}</span>
              </div>

              {plan.groups.length > 0 && (
                <table style={{ fontSize: "0.78rem", borderCollapse: "collapse", width: "100%", marginBottom: "0.75rem" }}>
                  <thead><tr style={{ color: "#888", textAlign: "left" }}>
                    <th style={{ padding: "4px 8px", fontWeight: 400 }}>{c.colRef}</th>
                    <th style={{ padding: "4px 8px", fontWeight: 400 }}>{c.colWork}</th>
                    <th style={{ padding: "4px 8px", fontWeight: 400 }}>{c.colImages}</th>
                    <th style={{ padding: "4px 8px", fontWeight: 400 }}>{c.colPdf}</th>
                  </tr></thead>
                  <tbody>
                    {plan.groups.slice(0, 10).map((g) => (
                      <tr key={g.artwork.id} style={{ borderTop: "1px solid #f0f0f0" }}>
                        <td style={{ padding: "4px 8px" }}>{g.artwork.inventory_number}</td>
                        <td style={{ padding: "4px 8px", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{g.artwork.title}</td>
                        <td style={{ padding: "4px 8px" }}>{g.images.length}</td>
                        <td style={{ padding: "4px 8px" }}>{g.pdfs.length}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {problems > 0 && (
                <ul style={{ fontSize: "0.78rem", color: "#a06010", margin: "0 0 0.75rem", paddingLeft: "1.1rem" }}>
                  {plan.match.unmatched.length > 0 && <li>{c.pUnmatched(plan.match.unmatched.length, plan.match.unmatched.slice(0, 3).map((e) => baseName(e.path)).join(", "))}</li>}
                  {plan.match.ambiguous.length > 0 && <li>{c.pAmbiguous(plan.match.ambiguous.length)}</li>}
                  {plan.match.unsupported.length > 0 && <li>{c.pUnsupported(plan.match.unsupported.length)}</li>}
                  {plan.match.tooLarge.length > 0 && <li>{c.pTooLarge(plan.match.tooLarge.length)}</li>}
                </ul>
              )}

              <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                <button style={btn(true)} disabled={!counts.images && !counts.pdfs} onClick={upload}>
                  {c.attach(counts.images + counts.pdfs)}
                </button>
                {problems > 0 && <button style={btn(false)} onClick={downloadReport}>{c.report}</button>}
                <button style={btn(false)} onClick={reset}>{c.cancel}</button>
              </div>
            </div>
          )}

          {step === "uploading" && (
            <p style={{ fontSize: "0.88rem", color: "#444" }}>
              {c.uploading(progress.current, progress.total, progress.label)}
            </p>
          )}

          {step === "done" && result && (
            <div style={{ border: "1px solid #b5d5b0", background: "#f0faf0", padding: "1rem 1.25rem", borderRadius: 2 }}>
              <p style={{ fontSize: "0.95rem", color: "#2a6a25", marginBottom: "0.5rem" }}>
                {c.done(result.imgOk, result.docOk, result.artOk)}
              </p>
              {result.errors.length > 0 && (
                <p style={{ fontSize: "0.82rem", color: "#8a3a22", marginBottom: "0.5rem" }}>
                  {c.errs(result.errors.length, result.errors[0].ref, result.errors[0].message)}
                </p>
              )}
              <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                {(problems > 0 || result.errors.length > 0) && <button style={btn(false)} onClick={downloadReport}>{c.report}</button>}
                <button style={btn(false)} onClick={reset}>{c.again}</button>
                <a href={`/collections/${collectionId}`} style={{ ...btn(true), textDecoration: "none", display: "inline-block" }}>{c.view}</a>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
